import { ChevronDown } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useRef, useState } from 'react';

export interface IFilterOption {
    value: string;
    label: string;
}

interface IProps {
    options: IFilterOption[];
    selectedValue: string | null;
    onSelect: (value: string) => void;
    placeholder?: string;
    hideOnSingleOption?: boolean;
    isLoading?: boolean;
}

const FilterDropdown = ({ options, selectedValue, onSelect, placeholder = 'Select', hideOnSingleOption = true, isLoading = false }: IProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedLabel = options.find((opt) => opt.value === selectedValue)?.label || placeholder;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);

        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    const handleSelect = (value: string) => {
        onSelect(value);
        setIsOpen(false);
    };

    if (hideOnSingleOption && options.length <= 1) return null;

    return (
        <div ref={containerRef} className="relative inline-block">
            <div className={`flex items-center gap-1.5 cursor-pointer ${isLoading ? 'custom-loading rounded' : ''}`} onClick={() => !isLoading && setIsOpen(!isOpen)}>
                <p className="text-grayLight900 text-xs leading-[18px] font-normal">{isLoading ? 'Loading...' : selectedLabel}</p>
                <ChevronDown className={`w-4 h-4 text-gray500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
            {isOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white border border-utilityGray200 rounded-xl shadow-lg z-10 min-w-[200px]">
                    <ul className="p-2">
                        {options.map((option) => (
                            <li
                                key={option.value}
                                className={`px-2.5 py-2 text-xs leading-5 text-grayLight900 hover:bg-gray50 rounded-md cursor-pointer ${
                                    selectedValue === option.value ? 'bg-gray50 font-medium' : 'font-normal'
                                }`}
                                onClick={() => handleSelect(option.value)}
                            >
                                {option.label}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default FilterDropdown;
